import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { APP_ENV } from '../config/app-environment';
import { I18nService } from '../i18n/i18n.service';
import { ToastService } from '../services/toast.service';
import { AuthService } from './auth.service';

export const httpErrorToastInterceptor: HttpInterceptorFn = (request, next) => {
  const environment = inject(APP_ENV);
  const auth = inject(AuthService);
  const toast = inject(ToastService);
  const i18n = inject(I18nService);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        toast.error(resolveHttpErrorMessage(error, i18n));

        if (error.status === 401 && request.url.startsWith(environment.apiBaseUrl)) {
          void auth.handleSessionExpired();
        }
      } else {
        toast.error(i18n.translate('toast.error.unexpected'));
      }

      return throwError(() => error);
    })
  );
};

function resolveHttpErrorMessage(error: HttpErrorResponse, i18n: I18nService): string {
  const payload = error.error;

  if (payload && typeof payload === 'object') {
    const errorMessage = payload['errorMessage'];
    const message = payload['message'];

    if (typeof errorMessage === 'string' && errorMessage.trim()) {
      return errorMessage;
    }

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (error.status === 0) {
    return i18n.translate('toast.error.network');
  }

  switch (error.status) {
    case 400:
      return i18n.translate('toast.error.badRequest');
    case 401:
      return i18n.translate('toast.error.unauthorized');
    case 403:
      return i18n.translate('toast.error.forbidden');
    case 404:
      return i18n.translate('toast.error.notFound');
    case 409:
      return i18n.translate('toast.error.conflict');
    case 422:
      return i18n.translate('toast.error.validation');
    case 500:
      return i18n.translate('toast.error.server');
    case 503:
      return i18n.translate('toast.error.unavailable');
  }

  return i18n.translate('toast.error.genericStatus', {
    status: error.status
  });
}
